import { type TypedDocumentNode, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { OLDEST_FIRST } from 'src/lib/pickerOrder';
import type { AddressRole, PlaceOptionsQuery, PlaceOptionsQueryVariables } from 'types/graphql';
import { useDebounceValue } from 'usehooks-ts';

/** How many places a picker shows before the user types. */
export const PLACE_OPTIONS_PAGE_SIZE = 50;
/** Typing pauses this long before the server search runs. */
export const PLACE_SEARCH_DEBOUNCE_MS = 300;

/**
 * A place picker's bounded read (TAS-3660 P7 BZ77): one `placePage` page under the picker's filters.
 * `ids` reads the selected place when it is not on the first page. The picker asks for oldest first (`OLDEST_FIRST`),
 * the order its unordered `places` list came back in.
 */
export const PLACE_OPTIONS_QUERY: TypedDocumentNode<PlaceOptionsQuery, PlaceOptionsQueryVariables> = gql`
  query PlaceOptionsQuery(
    $input: StandardPageInput!
    $addressRoles: [AddressRole]
    $businessEntityId: Int
    $strict: Boolean
    $ids: [Int]
  ) {
    placePage(
      input: $input
      addressRoles: $addressRoles
      businessEntityId: $businessEntityId
      strict: $strict
      ids: $ids
    ) {
      edges {
        node {
          ...PlaceAttributes
        }
      }
    }
  }
`;

export type PlaceOption = NonNullable<
  NonNullable<NonNullable<PlaceOptionsQuery['placePage']['edges']>[number]>['node']
>;

type PlaceOptionsArgs = {
  addressRoles?: AddressRole[];
  /** `undefined` spans every place; `null` is the organization's own places only. */
  businessEntityId?: number | null;
  strict?: boolean;
  /** What the user typed; the server search runs once typing pauses. */
  searchText?: string;
  /** The picker's current value: read by id when it is not on the page, so its label still shows. */
  selectedId?: number | null;
  /** Hold every read until the picker is used (e.g. its modal opens). */
  skip?: boolean;
};

const nodesOf = (data: PlaceOptionsQuery | undefined) =>
  (data?.placePage.edges ?? []).flatMap((edge) => (edge?.node ? [edge.node] : []));

/**
 * Search-as-you-type places for a picker (TAS-3660 P7 BZ77, P7-R35): the first page on open, then a
 * debounced server search, so an organization past the page size reaches every place by typing. The
 * previous options stay while a search is in flight.
 */
export function usePlaceOptions({
  addressRoles,
  businessEntityId,
  strict,
  searchText = '',
  selectedId,
  skip = false,
}: PlaceOptionsArgs): { places: PlaceOption[]; loading: boolean } {
  const [search] = useDebounceValue(searchText.trim(), PLACE_SEARCH_DEBOUNCE_MS);
  const filter = { addressRoles, businessEntityId, strict };
  const input = useMemo(
    () => ({ first: PLACE_OPTIONS_PAGE_SIZE, sortItems: OLDEST_FIRST, ...(search ? { searchText: search } : {}) }),
    [search]
  );
  const page = useQuery(PLACE_OPTIONS_QUERY, {
    variables: { input, ...filter },
    skip,
    // Fresh on open (the app default is cache-and-network); a pick returns to the first page without a re-read
    // (TAS-3660 P7-R53 request pins).
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: () => 'cache-first',
  });
  const places = useMemo(() => nodesOf(page.data ?? page.previousData), [page.data, page.previousData]);

  const selectedMissing = selectedId != null && !page.loading && !places.some((place) => place.id === selectedId);
  const selected = useQuery(PLACE_OPTIONS_QUERY, {
    variables: { input: { first: 1 }, ...filter, ids: [selectedId ?? 0] },
    skip: skip || !selectedMissing,
  });
  const extra = useMemo(() => (selectedMissing ? nodesOf(selected.data) : []), [selectedMissing, selected.data]);
  const options = useMemo(() => (extra.length ? [...extra, ...places] : places), [extra, places]);

  return { places: options, loading: page.loading };
}
